import { PaintBrush } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { RUNTIME_URI } from '../../../constants/platform';
import {
    type McrDeployResult,
    connectToEngineSocket,
    disconnectFromEngineSocket,
} from '../../../mcr/signalRConnection';
import { getActivatedForEnvironment } from '../../../sources/flow';
import translations from '../../../translations';
import type { Environment } from '../../../types/environment';
import type { FlowResponseAPI } from '../../../types/flow';
import type { ReleaseAPI, ReleaseElementAPI } from '../../../types/releases';
import { isNullOrEmpty, isNullOrUndefined } from '../../../utils/guard';
import { guid } from '../../../utils/guid';
import { stringReplace } from '../../../utils/string';
import { useAuth } from '../../AuthProvider';
import ButtonIcon from '../../buttons/ButtonIcon';
import CopyableText from '../../generic/CopyableText';
import FormGroup from '../../generic/FormGroup';
import Table, { type TableColumnList } from '../../generic/Table';
import ConfirmModal from '../../generic/modal/ConfirmModal';
import Modal from '../../generic/modal/GenericModal';
import FlowIcon from '../../icons/FlowIcon';
import Loader from '../../loader/Loader';
import { useEnvironments } from './EnvironmentsProvider';
import PreviewThemeModal from './PreviewThemeModal';
import { ENVIRONMENT_CATEGORY_TYPES } from './constants';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';

interface Props {
    release: ReleaseAPI;
    reload: () => void;
    environmentId: string | null;
    showRollback: boolean;
    passedEnvironments: Environment[];
}

const Release = ({ release, reload, environmentId, showRollback, passedEnvironments }: Props) => {
    const [show, toggle] = useState(false);
    const [loadedRelease, setRelease] = useState<ReleaseAPI | null>(null);
    const [showDeployModal, setShowDeployModal] = useState(false);
    const [environment, setCurrentEnvironment] = useState<Environment | null>(null);
    const [environmentToDeploy, setEnvironmentToDeploy] = useState<Environment | null>(null);
    const [deployed, setDeployed] = useState(false);
    const [rolledBack, setRolledBack] = useState(false);
    const [deployButtonHidden, setDeployButtonHidden] = useState(false);
    const [showRollbackModal, setShowRollbackModal] = useState(false);
    const [removeItemModalVisible, setRemoveItemModalVisible] = useState(false);
    const [itemToRemove, setItemToRemove] = useState<ReleaseElementAPI | null>(null);
    const [allowedToDelete, setAllowedToDelete] = useState(false);
    const [showDeleteReleaseModal, setShowDeleteReleaseModal] = useState(false);
    const [defaultPlayer, setDefaultPlayer] = useState<string | undefined>('');
    const [defaultTheme, setDefaultTheme] = useState<string | undefined>('');
    const [flows, setFlows] = useState<FlowResponseAPI[]>([]);
    const [selectedTheme, setSelectedTheme] = useState('');
    const [showRunThemeModal, setShowRunThemeModal] = useState(false);
    const [deploying, setDeploying] = useState(false);

    const {
        releaseLoading,
        getRelease,
        deploy,
        notifyRuntimesOnDeploy,
        rollback,
        environments,
        removeFlowFromRelease,
        removeThemeFromRelease,
        deleteRelease,
        tenantId,
        getAllEnvironments,
    } = useEnvironments();

    const dispatchNotification = useNotificationDispatch();

    const { tenant } = useAuth();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const currentEnvironment = Object.values(passedEnvironments ?? environments).find(
            (env) => env.id === release?.currentEnvironmentId,
        );
        setCurrentEnvironment(currentEnvironment ?? null);
        setDefaultPlayer(currentEnvironment?.defaultPlayerName);
        setDefaultTheme(currentEnvironment?.defaultThemeName);
    }, []);

    const toggleView = async () => {
        const visible = !show;
        toggle(visible);

        if (visible) {
            const rel = await getRelease(release.id);
            setRelease(rel);

            const currentEnvironment = Object.values(passedEnvironments ?? environments).find(
                (env) => env.id === rel?.currentEnvironmentId,
            );
            setCurrentEnvironment(currentEnvironment ?? null);

            if (
                !currentEnvironment ||
                currentEnvironment.classificationType === ENVIRONMENT_CATEGORY_TYPES.development
            ) {
                setAllowedToDelete(true);
            }

            const nextEnvironment = Object.values(passedEnvironments ?? environments).find(
                (environment) => environment.id === currentEnvironment?.nextEnvironmentId,
            );

            if (
                (environmentId && environmentId === currentEnvironment?.id && nextEnvironment) ||
                (isNullOrEmpty(environmentId) && nextEnvironment)
            ) {
                setDeployButtonHidden(false);
                setEnvironmentToDeploy(nextEnvironment);
            } else {
                setDeployButtonHidden(true);
            }
        }
    };

    const removeItem = async () => {
        if (isNullOrUndefined(itemToRemove?.themeId) && itemToRemove?.flowId) {
            await removeFlowFromRelease(release.id, itemToRemove.flowId);
        } else if (itemToRemove) {
            await removeThemeFromRelease(release.id, itemToRemove.themeId, itemToRemove.name);
        }

        await reload();
    };

    const onDeleteRelease = async () => {
        await deleteRelease(release.id);
        reload();
    };

    async function reloadWithEnvironments() {
        await getAllEnvironments();
        await reload();
    }

    async function deployRelease() {
        if (environmentToDeploy && loadedRelease) {
            setDeploying(true);
            if (environmentToDeploy.runtimeIds?.length > 0) {
                const messageId: string = guid();
                const publishPromise = connectToEngineSocket();

                await notifyRuntimesOnDeploy(
                    messageId,
                    loadedRelease.id,
                    environmentToDeploy.id,
                    false,
                );

                let deployResult: McrDeployResult | null = null;
                try {
                    deployResult = await publishPromise;

                    if (deployResult.messageId !== messageId) {
                        throw new Error('Returned Multi Cloud Runtime message ID does not match');
                    }
                } catch (error) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: { error },
                    });
                } finally {
                    if (deployResult?.success) {
                        setDeployed(true);
                    }
                    await disconnectFromEngineSocket();
                }
            } else {
                await deploy(loadedRelease.id, environmentToDeploy.id);
                setDeployed(true);
            }

            setDeploying(false);
        }
    }

    async function rollbackRelease() {
        if (loadedRelease && environment) {
            setDeploying(true);

            if (environment && environment.runtimeIds?.length > 0) {
                const messageId: string = guid();
                const publishPromise = connectToEngineSocket();

                await notifyRuntimesOnDeploy(messageId, loadedRelease.id, environment.id, true);

                let deployResult: McrDeployResult | null = null;
                try {
                    deployResult = await publishPromise;

                    if (deployResult.messageId !== messageId) {
                        throw new Error('Returned Multi Cloud Runtime message ID does not match');
                    }
                } catch (error) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: { error },
                    });
                } finally {
                    if (deployResult?.success) {
                        setRolledBack(true);
                    }
                    await disconnectFromEngineSocket();
                }
            } else {
                await rollback(
                    loadedRelease.id,
                    isNullOrEmpty(environmentId) ? environment.id : environmentId,
                );
                setRolledBack(true);
            }
            setDeploying(false);
        }
    }

    function getFlowUrl(flowId: string) {
        let url = '';
        if (!tenant?.tenantSettings?.themes) {
            url = `${RUNTIME_URI}/${tenantId}/play/${defaultPlayer}?flow-id=${flowId}&environment-id=${environment?.id}`;
        } else if (tenant?.tenantSettings?.themes) {
            url = `${RUNTIME_URI}/${tenantId}/play/theme/${defaultTheme}?flow-id=${flowId}&environment-id=${environment?.id}`;
        }
        return url;
    }

    async function openPreviewThemeModal(theme: string) {
        setSelectedTheme(theme);
        setShowRunThemeModal(!showRunThemeModal);

        if (environment) {
            setFlows(await getActivatedForEnvironment(environment.id));
        }
    }

    const columns: TableColumnList<ReleaseElementAPI> = [
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item }) => (
                <ButtonIcon
                    className="margin-left-small danger"
                    glyph="Delete"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => {
                        setItemToRemove(item);
                        setRemoveItemModalVisible(true);
                    }}
                    title={`Remove ${item.name}`}
                    hidden={deployButtonHidden}
                    iconClass="icon-medium"
                />
            ),
            size: '80px',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_type,
            renderCell: ({ item }) => {
                if (!isNullOrUndefined(item.flowId)) {
                    return <FlowIcon width="20" height="20" />;
                }
                if (!isNullOrUndefined(item.themeId)) {
                    return <PaintBrush width="20" height="20" />;
                }
                return <></>;
            },
        },
        {
            renderHeader: () => translations.COMMON_TABLE_name,
            renderCell: ({ item }) => item.name,
        },
        {
            renderHeader: () => translations.COMMON_TABLE_version_id,
            renderCell: ({ item }) => (
                <CopyableText copyableText={item.versionId} hasCopyButton={true} />
            ),
        },
        {
            renderHeader: () => translations.COMMON_TABLE_deployment_notes,
            renderCell: ({ item }) => item.summary,
            size: '300px',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_last_modified,
            renderCell: ({ item }) => {
                return item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '';
            },
        },
        {
            renderHeader: () => translations.ENVIRONMENT_releases_table_run_preview_header,
            renderCell: ({ item }) => {
                //Run a flow
                if (isNullOrEmpty(item.themeId)) {
                    const flowUrl = getFlowUrl(item.flowId);
                    return (
                        <a href={flowUrl} target="_blank" rel="noopener noreferrer">
                            {translations.ENVIRONMENT_run_in + environment?.name}
                        </a>
                    );
                }
                //Preview a Theme
                return (
                    <button
                        type="button"
                        className="btn btn-primary outcome"
                        onClick={() => openPreviewThemeModal(item.name)}
                    >
                        {translations.ENVIRONMENT_preview_in + environment?.name}
                    </button>
                );
            },
        },
    ];

    let content = <></>;
    if (loadedRelease) {
        let rows = loadedRelease.flows as ReleaseElementAPI[];
        if (!isNullOrUndefined(loadedRelease.themes)) {
            if (isNullOrUndefined(loadedRelease.flows)) {
                rows = loadedRelease.themes as ReleaseElementAPI[];
            } else {
                rows = rows.concat(loadedRelease.themes as ReleaseElementAPI[]);
            }
        }
        content = (
            <>
                <ConfirmModal
                    show={removeItemModalVisible}
                    title={translations.ENVIRONMENT_remove_item_confirmation_title}
                    messages={[
                        stringReplace(translations.ENVIRONMENT_remove_item_confirmation_message, {
                            itemName: itemToRemove?.name ?? '',
                            releaseName: release?.name ?? '',
                        }),
                    ]}
                    buttonStyle="danger"
                    buttonCaption="Remove"
                    onCancel={() => setRemoveItemModalVisible(false)}
                    onConfirm={() => {
                        removeItem();
                        setRemoveItemModalVisible(false);
                    }}
                />

                <ConfirmModal
                    show={showDeleteReleaseModal}
                    title={translations.ENVIRONMENT_delete_release}
                    messages={[
                        stringReplace(
                            translations.ENVIRONMENT_delete_release_confirmation_message,
                            {
                                releaseName: release?.name ?? '',
                            },
                        ),
                    ]}
                    buttonStyle="danger"
                    buttonCaption="Delete"
                    onCancel={() => setShowDeleteReleaseModal(false)}
                    onConfirm={() => {
                        onDeleteRelease();
                        setShowDeleteReleaseModal(false);
                    }}
                />
                <Table columns={columns} items={rows} isLoading={releaseLoading} />
                {!deployButtonHidden && environmentToDeploy ? (
                    <button
                        type="button"
                        className="btn btn-primary outcome margin-right"
                        onClick={() => setShowDeployModal(!showDeployModal)}
                    >
                        {translations.ENVIRONMENT_promote + environmentToDeploy.name}
                    </button>
                ) : null}
                {showRollback ? (
                    <button
                        type="button"
                        className="btn btn-default outcome"
                        onClick={() => setShowRollbackModal(!showRollbackModal)}
                    >
                        {translations.ENVIRONMENT_rollback}
                    </button>
                ) : null}
                {allowedToDelete ? (
                    <button
                        type="button"
                        className="btn btn-default outcome"
                        onClick={() => setShowDeleteReleaseModal(!showRollbackModal)}
                    >
                        {translations.ENVIRONMENT_delete_release}
                    </button>
                ) : null}
                <Modal
                    show={showDeployModal}
                    onHide={() => setShowDeployModal(!showDeployModal)}
                    title={
                        deployed
                            ? translations.ENVIRONMENT_promote_release_success
                            : translations.ENVIRONMENT_promote_release
                    }
                    className="config-modal"
                    renderBody={() =>
                        deployed ? (
                            <p>
                                {[
                                    stringReplace(
                                        translations.ENVIRONMENT_promote_release_message,
                                        {
                                            releaseName: release?.name ?? '',
                                            environmentName: environmentToDeploy?.name ?? '',
                                        },
                                    ),
                                ]}
                            </p>
                        ) : (
                            <>
                                {deploying ? (
                                    <Loader message="Deploying release" />
                                ) : (
                                    <>
                                        <p>
                                            {[
                                                stringReplace(
                                                    translations.ENVIRONMENT_promote_subtext,
                                                    {
                                                        releaseName: release?.name ?? '',
                                                    },
                                                ),
                                            ]}
                                        </p>
                                        <FormGroup
                                            label={
                                                translations.ENVIRONMENT_promote_target_environment
                                            }
                                            htmlFor="deployment-environment"
                                        >
                                            <input
                                                id="deployment-environment"
                                                className="form-control form-control-dynamic"
                                                value={
                                                    environmentToDeploy
                                                        ? environmentToDeploy.name
                                                        : ''
                                                }
                                                type="text"
                                                readOnly
                                            />
                                        </FormGroup>
                                    </>
                                )}
                            </>
                        )
                    }
                    renderFooter={() =>
                        !deployed && environmentToDeploy ? (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-default outcome"
                                    onClick={() => setShowDeployModal(!showDeployModal)}
                                    disabled={deploying}
                                >
                                    {translations.ENVIRONMENT_cancel}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary outcome"
                                    onClick={() => deployRelease()}
                                    disabled={deploying}
                                >
                                    {translations.ENVIRONMENT_promote + environmentToDeploy.name}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-primary outcome"
                                onClick={() => reloadWithEnvironments()}
                            >
                                {translations.ENVIRONMENT_finish}
                            </button>
                        )
                    }
                />
                <Modal
                    show={showRollbackModal}
                    onHide={() => setShowRollbackModal(!showRollbackModal)}
                    title={translations.ENVIRONMENT_rollback_release}
                    bodyClassName="modal-body-no-height"
                    renderBody={() =>
                        deploying ? (
                            <Loader message="Rolling back release" />
                        ) : (
                            <p>
                                {rolledBack
                                    ? `${loadedRelease.name} was successfully rolled back from ${environment?.name}`
                                    : translations.ENVIRONMENT_rollback_release_confirmation +
                                      release.name}
                            </p>
                        )
                    }
                    className="config-modal"
                    renderFooter={() =>
                        rolledBack ? (
                            <button
                                type="button"
                                className="btn btn-primary outcome"
                                onClick={() => reloadWithEnvironments()}
                            >
                                {translations.ENVIRONMENT_finish}
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-default outcome"
                                    onClick={() => setShowRollbackModal(!showRollbackModal)}
                                    disabled={deploying}
                                >
                                    {translations.ENVIRONMENT_cancel}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-danger outcome"
                                    onClick={() => rollbackRelease()}
                                    disabled={deploying}
                                >
                                    {translations.ENVIRONMENT_rollback}
                                </button>
                            </>
                        )
                    }
                />
                <PreviewThemeModal
                    showRunThemeModal={showRunThemeModal}
                    setShowRunThemeModal={setShowRunThemeModal}
                    flows={flows}
                    selectedTheme={selectedTheme}
                    environment={environment}
                />
            </>
        );
    }

    return (
        <div className="expandable-area margin-bottom margin-right-sml">
            <div
                className="expandable-header margin-left"
                onClick={toggleView}
                onKeyDown={toggleView}
                // biome-ignore lint/a11y/useSemanticElements: Requires refactor
                role="button"
                tabIndex={0}
            >
                <div className="inline-block">
                    <h5 className="inline-block margin-right">{`${release.name}  `}</h5>
                    {release.lastDeployed
                        ? `Last deployed: ${new Date(release.lastDeployed).toLocaleString()} to: ${
                              environment?.name ?? ENVIRONMENT_CATEGORY_TYPES.development
                          }`
                        : 'Not deployed'}
                </div>
                <span
                    className={
                        show
                            ? 'float-right padding padding-top glyphicon glyphicon-triangle-bottom rotate180'
                            : 'float-right padding padding-top glyphicon glyphicon-triangle-bottom'
                    }
                    aria-hidden="true"
                />
            </div>
            <div className={show ? 'expand-show padding-default' : 'expand-hide'}>{content}</div>
        </div>
    );
};

export default Release;
